import CT from '@marketplace-ai/tags';
import { ColDef, DoesFilterPassParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Pill } from '~/components';
import { dateComparator } from '~/components/agGrid/columns/dateColumns';
import { createValidCol } from '~/components/agGrid/columns/sharedColumns';
import { CustomDateRangeFilter, CustomSetFilter } from '~/components/agGrid/filters';
import { ACTIONS_COLUMN_BASE, ActionsRenderer, type ActionDef } from '~/components/Table/gridUtil';
import { dateStringFormatter } from '../../../utils';
import { MalpracticeInsuranceFormValues } from './types';

const getStatusLabel = (expiryDate?: string) =>
  expiryDate && new Date(expiryDate) > new Date() ? 'Active' : 'Inactive';

export const createInsuranceCols = ({
  handleDelete,
  handleEdit,
}: {
  handleDelete: (id: number) => void;
  handleEdit: (id: number) => void;
}): ColDef<MalpracticeInsuranceFormValues>[] => [
  createValidCol(),
  {
    field: 'id',
    hide: true,
  },
  {
    headerName: 'Insurance Company',
    field: 'insuranceCompany',
  },
  {
    headerName: 'Policy Number',
    field: 'policyNumber',
  },
  {
    // [MCV-1753] Explicit colId: an id-less column is matched across colDefs rebuilds only by
    // object identity, so it lost its place whenever the page re-rendered.
    colId: 'coveragePeriod',
    headerName: 'Coverage Period',
    cellRenderer: ({ data }: CustomCellRendererProps<MalpracticeInsuranceFormValues>) => {
      if (data) {
        return (
          dateStringFormatter(data.effectiveDate) + ' - ' + dateStringFormatter(data.expiryDate)
        );
      }
    },
    filter: {
      component: CustomDateRangeFilter,
      doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
        if (!model || (!model.startDate && !model.endDate)) return true;
        const effective = node.data?.effectiveDate ? new Date(node.data.effectiveDate) : null;
        const expiry = node.data?.expiryDate ? new Date(node.data.expiryDate) : null;
        const from = model.startDate ? new Date(model.startDate) : null;
        const to = model.endDate ? new Date(model.endDate) : null;
        if (from && expiry && expiry < from) return false;
        if (to && effective && effective > to) return false;
        return true;
      },
    },
    valueGetter: ({ data }) => {
      if (data) {
        const date = data.effectiveDate;
        return new Date(date);
      }
    },
    comparator: dateComparator,
  },
  {
    colId: 'status',
    headerName: 'Status',
    cellRenderer: ({ data }: CustomCellRendererProps<MalpracticeInsuranceFormValues>) => {
      if (data) {
        return (
          <Pill
            label={getStatusLabel(data.expiryDate)}
            customStyle="bg-white text-black ring-grey-300/20"
          />
        );
      }
    },
    filter: {
      component: CustomSetFilter,
      doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
        if (!model || model.length === 0) return true;
        return model.includes(getStatusLabel(node.data?.expiryDate));
      },
    },
    valueGetter: ({ data }) => getStatusLabel(data?.expiryDate),
    comparator: (valueA: string, valueB: string) => valueA.localeCompare(valueB),
  },
  {
    ...ACTIONS_COLUMN_BASE,
    width: 80,
    cellRenderer: ActionsRenderer,
    cellRendererParams: {
      actions: [
        { type: 'edit', handler: handleEdit, cyTag: CT.malpracticeInsurance.table.editBtn },
        { type: 'delete', handler: handleDelete, cyTag: CT.malpracticeInsurance.table.deleteBtn },
      ] satisfies ActionDef[],
    },
  },
];
